import { InstrumentType, OrderSide, notEmpty } from "@orb-labs/types";
import { extractStandardizedInstrument, extractWalletProfile } from "@orb-labs/orby-core";
import { extractSignalEdge } from "../../hooks/bullrunQueries/useListSignalsQuery.js";
/**
 * Discriminant for the Signal feed's two row classes. Trades are
 * append-only (a trade never changes); clusters re-fire under a stable id
 * as the cohort grows and must be upserted in place.
 */
export var SignalFeedItemKind;
(function (SignalFeedItemKind) {
    SignalFeedItemKind["TRADE"] = "trade";
    SignalFeedItemKind["CLUSTER"] = "cluster";
})(SignalFeedItemKind || (SignalFeedItemKind = {}));
/**
 * Parse a wire timestamp to epoch ms. Returns `undefined` (never a 0
 * placeholder) when the value is missing or unparseable — callers decide
 * how to handle a missing timestamp explicitly.
 */
function parseTimestampMs(value) {
    if (!notEmpty(value))
        return undefined;
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : undefined;
}
function logClusterDrop(raw, reason, error) {
    // Mirrors `extractSignalEdge`'s logged-drop pattern (console.error with
    // the row id) — malformed venue/server data must never disappear silently.
    console.error("[extractSignalFeedItem] dropping malformed cluster", {
        id: raw.id,
        reason,
        ...(notEmpty(error) ? { error } : {}),
    });
    return undefined;
}
/**
 * Convert one raw GraphQL `SignalsItem` (query edge or subscription event)
 * into the discriminated SDK shape. Cluster rows ride on
 * `activityItem.__typename === "SignalCluster"` (host `profile` is null
 * for them); everything else routes through `extractSignalEdge`.
 * Malformed rows degrade to a logged drop, mirroring `extractSignalEdge`.
 */
export function extractSignalFeedItem(raw) {
    const item = raw;
    if (item?.activityItem?.__typename === "SignalCluster") {
        const activity = item.activityItem;
        try {
            if (!notEmpty(activity.clusterId))
                return logClusterDrop(item, "missing clusterId");
            const traderCount = Number(activity.traderCount);
            const totalNotionalUsd = Number(activity.totalNotionalUsd);
            if (!Number.isFinite(traderCount) || !Number.isFinite(totalNotionalUsd)) {
                return logClusterDrop(item, "non-finite traderCount/totalNotionalUsd");
            }
            // Both timestamps are required: `lastEventAtMs` drives feed ordering
            // and the out-of-order upsert guard, and a cluster the server stamped
            // without parseable timestamps is malformed — drop it loudly rather
            // than sink a silent 0 into ordering.
            const windowStartedAtMs = parseTimestampMs(activity.windowStartedAt);
            const lastEventAtMs = parseTimestampMs(activity.lastEventAt);
            if (!notEmpty(windowStartedAtMs) || !notEmpty(lastEventAtMs)) {
                return logClusterDrop(item, "invalid windowStartedAt/lastEventAt timestamp");
            }
            // Enum membership is validated (not cast) so a server value this
            // client version doesn't know — schema drift, future migration —
            // degrades to a logged drop instead of rendering an invalid value.
            const instrumentType = String(item.instrumentType);
            if (!Object.values(InstrumentType).includes(instrumentType)) {
                return logClusterDrop(item, `unknown instrumentType "${instrumentType}"`);
            }
            const side = String(activity.side);
            if (!Object.values(OrderSide).includes(side)) {
                return logClusterDrop(item, `unknown side "${side}"`);
            }
            const sampleProfiles = Array.isArray(activity.sampleProfiles)
                ? activity.sampleProfiles.map((profile) => extractWalletProfile(profile)).filter(notEmpty)
                : [];
            return {
                kind: SignalFeedItemKind.CLUSTER,
                cluster: {
                    id: String(activity.clusterId),
                    instrumentType: instrumentType,
                    instrument: extractStandardizedInstrument(item.instrument),
                    side: side,
                    traderCount,
                    totalNotionalUsd,
                    windowStartedAtMs,
                    lastEventAtMs,
                    sampleProfiles,
                },
            };
        }
        catch (error) {
            return logClusterDrop(item, "extractor threw", error);
        }
    }
    const edge = extractSignalEdge(raw);
    if (!notEmpty(edge))
        return undefined;
    return { kind: SignalFeedItemKind.TRADE, edge };
}
/** Stable per-item id — trade composite id or clusterId. */
export function signalFeedItemId(item) {
    return item.kind === SignalFeedItemKind.TRADE ? item.edge.id : item.cluster.id;
}
/**
 * Out-of-order guard for cluster upserts: a re-fire is only "newer" when
 * its `lastEventAtMs` advances (ties/regressions fall back to the larger
 * `traderCount` — the cohort only ever grows within one window). Guards
 * against e.g. cache-and-network replaying a cached page-1 cluster after
 * the live stream already escalated it, which would visibly downgrade the
 * wallet count.
 */
export function isNewerClusterSnapshot(candidate, baseline) {
    if (candidate.lastEventAtMs !== baseline.lastEventAtMs) {
        return candidate.lastEventAtMs > baseline.lastEventAtMs;
    }
    return candidate.traderCount > baseline.traderCount;
}
/**
 * Resolve a cluster upsert: returns the NEWER of the existing/incoming
 * snapshots (see `isNewerClusterSnapshot`). Position semantics are the
 * caller's — only the payload regression is guarded here.
 */
export function preferNewerCluster(existing, incoming) {
    if (existing.kind !== SignalFeedItemKind.CLUSTER || incoming.kind !== SignalFeedItemKind.CLUSTER) {
        return incoming;
    }
    return isNewerClusterSnapshot(incoming.cluster, existing.cluster) ? incoming : existing;
}
/**
 * Merge one live subscription item into the feed list. Trades prepend
 * (the caller's seen-id set already filters duplicates); cluster re-fires
 * REPLACE the existing row and bump it to the top — the FOMO-style
 * escalation where "3 wallets" becomes "5 wallets" in place instead of
 * stacking duplicate rows. When the incoming cluster snapshot is OLDER
 * than the one already shown (out-of-order delivery), the existing payload
 * is kept but still moves to the incoming's position — position semantics
 * are identical either way; only the payload regression is guarded.
 */
export function mergeLiveSignalFeedItem(prev, incoming) {
    const id = signalFeedItemId(incoming);
    const existingIndex = prev.findIndex((item) => signalFeedItemId(item) === id);
    if (existingIndex < 0)
        return [incoming, ...prev];
    const chosen = preferNewerCluster(prev[existingIndex], incoming);
    const next = [...prev];
    next.splice(existingIndex, 1);
    return [chosen, ...next];
}
